/**
 * Deterministic PRNG (mulberry32) so mock data is stable across renders/reloads
 * instead of reshuffling every time a component mounts.
 */
export function createRng(seed: number) {
  let a = seed >>> 0;

  const next = () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  return {
    next,
    int(min: number, max: number) {
      return Math.floor(next() * (max - min + 1)) + min;
    },
    float(min: number, max: number, precision = 2) {
      const v = next() * (max - min) + min;
      return Number(v.toFixed(precision));
    },
    bool(probability = 0.5) {
      return next() < probability;
    },
    pick<T>(arr: readonly T[]): T {
      return arr[Math.floor(next() * arr.length)];
    },
    pickWeighted<T>(entries: Array<[T, number]>): T {
      const total = entries.reduce((sum, [, w]) => sum + w, 0);
      let roll = next() * total;
      for (const [value, weight] of entries) {
        roll -= weight;
        if (roll <= 0) return value;
      }
      return entries[entries.length - 1][0];
    },
    shuffle<T>(arr: readonly T[]): T[] {
      const copy = [...arr];
      for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(next() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      return copy;
    },
    sample<T>(arr: readonly T[], count: number): T[] {
      return this.shuffle(arr).slice(0, count);
    },
    daysAgo(maxDays: number, minDays = 0) {
      const days = this.int(minDays, maxDays);
      const d = new Date();
      d.setDate(d.getDate() - days);
      return d.toISOString();
    },
    daysFromNow(maxDays: number, minDays = 0) {
      const days = this.int(minDays, maxDays);
      const d = new Date();
      d.setDate(d.getDate() + days);
      return d.toISOString();
    },
  };
}

export type Rng = ReturnType<typeof createRng>;

/** simple string hash to derive a numeric seed from an id, for per-entity sub-data */
export function seedFromString(input: string) {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) || 1;
}
